import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Box, Chip, Stack, Typography, Paper, Divider } from '@mui/material';
import ButtonLoading from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import Visibility from '@mui/icons-material/VisibilityOutlined';
import ContactlessOutlinedIcon from '@mui/icons-material/ContactlessOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Formik, FormikProps } from 'formik';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { valueSafeDecryptor, valueSafeEcryptor } from 'utils/Encryptor';
import { PATH_CONSTANT } from 'constant/PathConstant';
import {
  useAddServer,
  useGetServerDetails,
  useServerMemberListDropdown,
  useTestServerConnection,
} from 'services/v1/TenantMaster/MasterTable/ServerTableDataService';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { DropdownCreatableMemberList } from '../../../Tenant/AROKMS/DataInput/components/DropdownCreatableMemberList';
import { RULES_CONSTANT } from 'constant/RuleConstant';
import { Field } from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import { useParams } from 'react-router-dom';
import ActivityIndicator from 'components/ActivityIndicatorComponent';

const sectionPaperStyles = {
  p: 2.5,
  border: '1px solid #E3EBF6',
  borderRadius: 2,
  boxShadow: 'none',
};

const sectionHeaderStyles = {
  color: '#3B4797',
  fontWeight: 600,
  fontSize: 14,
};

const sectionDescriptionStyles = {
  color: '#6B7280',
  fontSize: 12,
  mt: 0.5,
};

const fieldLabelStyles = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  minWidth: 140,
  mb: 0.5,
};

interface FormValue {
  serverName: DropdownItem | null;
  description: string;
  host: string;
  username: string;
  password: string;
  port: string;
}

const formInitialValues: FormValue = {
  serverName: null,
  description: '',
  host: '',
  username: '',
  password: '',
  port: '',
};

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <Box mb={2}>
      <Typography sx={sectionHeaderStyles}>{title}</Typography>
      <Typography sx={sectionDescriptionStyles}>{description}</Typography>
    </Box>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5} sx={{ mt: 2 }}>
      <Typography sx={fieldLabelStyles}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </Typography>
      {children}
    </Stack>
  );
}

export default function EditServerTableData() {
  const { id } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const formikRef = useRef<FormikProps<FormValue>>(null);
  const navigate = useNavigate();

  const { data: serverMemberListOptions } = useServerMemberListDropdown();
  const { mutate: createNewServer, isLoading: isDeploying } = useAddServer();
  const { data: serverDetailsData, isLoading } = useGetServerDetails(id);
  const {
    mutate: testServerConnection,
    isLoading: isTesting,
    isSuccess: isServerConnected,
    isError: isServerConnectionError,
    error: serverConnectionError,
    data: serverConnectionData,
  } = useTestServerConnection();

  const handleOnTestConnection = () => {
    const payloadData = formikRef?.current?.values;
    payloadData &&
      payloadData.username &&
      payloadData.password &&
      payloadData.host &&
      testServerConnection({
        ...payloadData,
        serverName: payloadData?.serverName?.value as string,
        password: valueSafeEcryptor(payloadData?.password) as string,
        username: valueSafeEcryptor(payloadData?.username) as string,
      });
  };

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  const handleOnSave = (data: FormValue) => {
    createNewServer(
      {
        ...data,
        id,
        serverName: data?.serverName?.value || '',
        password: valueSafeEcryptor(data?.password) as string,
        username: valueSafeEcryptor(data?.username) as string,
      },
      {
        onSuccess: () => {
          handleOnCancelInput();
        },
      },
    );
  };

  const handleOnCancelInput = () => {
    navigate(PATH_CONSTANT.TENANT_MASTER.MASTER_TABLE.SERVER);
  };

  // @ts-ignore
  const fieldItem: Field = useMemo(() => {
    if (serverMemberListOptions) {
      return {
        name: 'serverName',
        options: serverMemberListOptions.data.options,
        label: 'Server',
        disabled: true,
        placeholder: 'Server Name',
        colSubjectId: serverMemberListOptions.data.colSubjectId,
      };
    }
    return {} as Field;
  }, [serverMemberListOptions]);

  useEffect(() => {
    if (formikRef.current) {
      if (serverDetailsData && serverMemberListOptions) {
        const { serverName, serverDescription, host, username, port, password } = serverDetailsData.data;
        const serverNameValue = serverMemberListOptions?.data.options.find((item) => item.value === serverName);
        formikRef?.current?.setValues({
          serverName: serverNameValue as DropdownItem,
          description: serverDescription,
          host,
          username,
          password: valueSafeDecryptor(password) || '',
          port,
        });
        handleOnTestConnection();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverDetailsData, formikRef, serverMemberListOptions]);

  return (
    <TenantHomeDashboardNavigationBar>
      <Box sx={{ height: '100%', backgroundColor: '#F8F9FC' }}>
        {isLoading ? (
          <Stack direction='column' sx={{ height: 500 }} justifyContent='center'>
            <ActivityIndicator />
          </Stack>
        ) : (
          <Formik innerRef={formikRef} initialValues={formInitialValues} onSubmit={handleOnSave}>
            {(formikProps: FormikProps<FormValue>) => {
              const { handleSubmit, values, handleChange, errors, touched, handleBlur } = formikProps;
              const allFieldsFilled = !Object.values(values).some((value) => !value);
              return (
                <Box sx={{ maxWidth: 820, mx: 'auto', py: 3, px: 2 }}>
                  {/* Page Header */}
                  <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
                    <Box>
                      <Stack direction='row' alignItems='center' spacing={1.5}>
                        <Typography
                          variant='body1'
                          component='h2'
                          fontWeight='bold'
                          sx={{ color: '#3B4797', fontSize: 18 }}
                        >
                          Edit Server
                        </Typography>
                        {values.serverName?.label && (
                          <Chip
                            label={values.serverName.label}
                            size='small'
                            sx={{ backgroundColor: '#EDF2FF', color: '#3B4797', fontWeight: 500, fontSize: 12 }}
                          />
                        )}
                      </Stack>
                      <Typography sx={{ color: '#6B7280', fontSize: 13, mt: 0.5 }}>
                        Update server connection details and verify the connection. The server name cannot be changed
                        after creation.
                      </Typography>
                    </Box>
                    <Button
                      size='small'
                      onClick={handleOnCancelInput}
                      variant='main-table-panel-border'
                      disabled={isDeploying}
                    >
                      Cancel
                    </Button>
                  </Stack>

                  {/* Section 1: Server Identity */}
                  <Paper sx={sectionPaperStyles}>
                    <SectionHeader
                      title='Server Identity'
                      description='The server name was set during creation and cannot be changed. You can update the description.'
                    />
                    <Divider sx={{ mb: 1 }} />

                    <DropdownCreatableMemberList
                      field={fieldItem}
                      // @ts-ignore
                      formik={formikProps}
                      ruleTypeCode={RULES_CONSTANT.BASE_SUBJECT.RULE_ATTRIBUTE_SUBJECT_BASE}
                      sx={{ width: '100%' }}
                    />

                    <FormField label='Description' required>
                      <TextField
                        fullWidth
                        size='small'
                        disabled={isDeploying}
                        multiline
                        minRows={2}
                        placeholder='e.g. Production database server for APAC region'
                        onChange={handleChange}
                        value={values.description}
                        onBlur={handleBlur}
                        name='description'
                        error={touched.description && Boolean(errors.description)}
                        helperText={touched.description && errors.description}
                      />
                    </FormField>
                  </Paper>

                  {/* Section 2: Connection Details */}
                  <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                    <SectionHeader
                      title='Connection Details'
                      description='Update the database connection parameters. Credentials are encrypted before storage.'
                    />
                    <Divider sx={{ mb: 1 }} />

                    <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                      <Stack spacing={0.5} sx={{ flex: 3 }}>
                        <Typography sx={fieldLabelStyles}>
                          Host <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          disabled={isDeploying}
                          placeholder='e.g. server.yourdomain.com'
                          onChange={handleChange}
                          value={values?.host}
                          onBlur={handleBlur}
                          name='host'
                          error={touched.host && Boolean(errors.host)}
                          helperText={touched.host && errors.host}
                        />
                      </Stack>
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelStyles}>
                          Port <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          disabled={isDeploying}
                          placeholder='e.g. 5432'
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.match(/^[0-9]*$/)) {
                              handleChange(e);
                            }
                          }}
                          onBlur={handleBlur}
                          value={values?.port}
                          name='port'
                          error={touched.port && Boolean(errors.port)}
                          helperText={touched.port && errors.port}
                        />
                      </Stack>
                    </Stack>

                    <Stack direction='row' spacing={2} sx={{ mt: 2 }}>
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelStyles}>
                          Username <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          disabled={isDeploying}
                          placeholder='Database username'
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values?.username}
                          name='username'
                          error={touched.username && Boolean(errors.username)}
                          helperText={touched.username && errors.username}
                        />
                      </Stack>
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography sx={fieldLabelStyles}>
                          Password <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          size='small'
                          disabled={isDeploying}
                          type={showPassword ? 'text' : 'password'}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder='Database password'
                          value={values?.password}
                          name='password'
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position='end'>
                                <IconButton onClick={handlePasswordToggle} size='small'>
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Paper>

                  {/* Section 3: Connection Test */}
                  <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
                    <SectionHeader
                      title='Verify Connection'
                      description='Test the database connection to ensure the server is reachable with the updated credentials.'
                    />
                    <Divider sx={{ mb: 2 }} />

                    <Stack direction='row' alignItems='center' spacing={2}>
                      <ButtonLoading
                        disabled={!allFieldsFilled}
                        variant='main-table-panel'
                        loading={isTesting}
                        startIcon={<ContactlessOutlinedIcon />}
                        onClick={handleOnTestConnection}
                      >
                        Test Connection
                      </ButtonLoading>

                      {serverConnectionData !== undefined && isServerConnected && (
                        <Chip
                          icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                          label='Connected'
                          color='success'
                          sx={{ bgcolor: '#42BB93', fontWeight: 500 }}
                        />
                      )}

                      {serverConnectionData === undefined && isServerConnectionError && (
                        <Chip
                          icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
                          // @ts-ignore
                          label={serverConnectionError?.response?.data?.message || 'Connection failed'}
                          color='error'
                          variant='outlined'
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                    </Stack>

                    {serverConnectionData === undefined && !isServerConnectionError && !isTesting && (
                      <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 1.5 }}>
                        Click "Test Connection" to verify the server is reachable.
                      </Typography>
                    )}
                  </Paper>

                  {/* Action Buttons */}
                  <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 3 }}>
                    <Button disabled={isDeploying} onClick={handleOnCancelInput} variant='main-table-panel-border'>
                      Cancel
                    </Button>
                    <ButtonLoading
                      disabled={!allFieldsFilled || !isServerConnected}
                      variant='main-table-panel'
                      loading={isDeploying}
                      onClick={() => handleSubmit()}
                    >
                      Save Changes
                    </ButtonLoading>
                  </Stack>
                </Box>
              );
            }}
          </Formik>
        )}
      </Box>
    </TenantHomeDashboardNavigationBar>
  );
}
